import React from 'react'
import { HorizontalBar } from 'react-chartjs-2'
import 'chartjs-plugin-datalabels'
import { useTranslation } from 'react-i18next'
import { getYearTranslationKey } from '@dataplace.ai/functions/utils'
import CatchmentInfoComponent from '../components/CatchmentInfoComponent'
import { ChartContainer, Header, RightSection, StyledStrong } from './OverviewDemography.styles'
import { useDemographyChart } from './hooks/useDemographyChart'
import { StyledWrapper, TitleWrapper } from '../SharedOverview.styles'
import { NoDataGuard } from '../components/NoDataGuard'
import { NO_OVERVIEW_DATA } from '../constants'

export interface GenderData {
  share: number
  value: number
}

export interface Catchment {
  mode: string
  length: number
}

export interface AgeBucket {
  order: number
  ageRange: string
  share: number
  value: number
  female: GenderData
  male: GenderData
}

interface OverviewDemographyProps {
  catchment: Catchment
  averageAge: number
  ageBuckets: AgeBucket[]
}

export type OverviewDemographyData = OverviewDemographyProps | typeof NO_OVERVIEW_DATA
const TILE_TITLE_KEY = 'placeme.tile.demography'

const OVERVIEW_DEMOGRAPHY = ({
  catchment, averageAge, ageBuckets,
}: OverviewDemographyProps): JSX.Element => {
  const { t } = useTranslation()

  const roundedAge = Math.round(averageAge)
  const getChartHeight = (buckets: AgeBucket[], rowHeight = 33) => buckets.length * rowHeight
  const chartHeight = getChartHeight(ageBuckets)
  const {
    data, options,
  } = useDemographyChart(ageBuckets)

  return (
    <StyledWrapper>
      <Header>
        <TitleWrapper>
          {t(TILE_TITLE_KEY)}
          <CatchmentInfoComponent {...catchment} />
        </TitleWrapper>
        <RightSection>
          {t('placeme.demography_tile.banner')}
          <StyledStrong>
            {roundedAge}
            {' '}
            {t(getYearTranslationKey(roundedAge))}
          </StyledStrong>
        </RightSection>
      </Header>
      <ChartContainer heightPx={chartHeight}>
        <HorizontalBar
          data={data}
          options={options}
        />
      </ChartContainer>
    </StyledWrapper>
  )
}

export function OverviewDemography({ data }: {data: OverviewDemographyData}): JSX.Element {
  return (
    <NoDataGuard
      component={OVERVIEW_DEMOGRAPHY}
      data={data}
      title={TILE_TITLE_KEY}
    />
  )
}
